import { db, isFirebaseConfigured, storage } from '../config/firebase';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc,
  query, 
  where, 
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

const toProductId = (value) => {
  const base = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'product';
};

const normalizeProduct = (p) => {
  if (!p) return p;

  const categories = Array.isArray(p.categories)
    ? p.categories
    : typeof p.category === 'string' && p.category
      ? [p.category]
      : [];

  const rawVariantStock = p?.variantStock && typeof p.variantStock === 'object' ? p.variantStock : null;
  const variantStock = rawVariantStock
    ? Object.fromEntries(
        Object.entries(rawVariantStock).map(([k, v]) => {
          const qty = Number(v);
          return [String(k), Number.isFinite(qty) ? qty : 0];
        })
      )
    : null;

  const totalVariantStock = variantStock ? Object.values(variantStock).reduce((sum, n) => sum + (Number(n) || 0), 0) : null;
  const derivedInStock = variantStock ? totalVariantStock > 0 : Number(p?.stockQuantity || 0) > 0;
  const stockQuantity = variantStock ? totalVariantStock : Number(p?.stockQuantity || 0);

  const rawColorImages = p?.colorImages && typeof p.colorImages === 'object' ? p.colorImages : null;
  const colorImages = rawColorImages
    ? Object.fromEntries(
        Object.entries(rawColorImages).map(([rawKey, rawValue]) => {
          const key = String(rawKey || '').toUpperCase();
          const v = rawValue && typeof rawValue === 'object' ? rawValue : {};
          const imgs = Array.isArray(v.images) ? v.images.filter((x) => typeof x === 'string' && x) : [];
          const idx = Number(v.primaryImageIndex ?? 0);
          return [
            key,
            {
              images: imgs,
              primaryImageIndex: Number.isFinite(idx) && idx >= 0 ? idx : 0,
            },
          ];
        })
      )
    : undefined;

  return {
    ...p,
    categories,
    category: typeof p.category === 'string' && p.category ? p.category : categories[0],
    visible: p.visible !== false,
    variantStock: variantStock || undefined,
    stockQuantity: Number.isFinite(Number(stockQuantity)) ? Number(stockQuantity) : 0,
    inStock: typeof p.inStock === 'boolean' ? p.inStock : derivedInStock,
    images: Array.isArray(p.images) ? p.images : [],
    colorImages,
    sizes: Array.isArray(p.sizes) && p.sizes.length > 0 ? p.sizes : ['S', 'M', 'L'],
    colors: Array.isArray(p.colors) && p.colors.length > 0 ? p.colors : ['BLACK'],
  };
};

const isStorefrontVisible = (p) => {
  if (!p) return false;
  if (p.deleted) return false;
  if (p.status === 'draft') return false;
  return p.visible !== false;
};

// Get all products
export const getProducts = async () => {
  // If Firebase not configured, do not show mock products
  if (!isFirebaseConfigured || !db) {
    return [];
  }

  try {
    const productsRef = collection(db, 'products');
    const querySnapshot = await getDocs(productsRef);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If no products in Firebase, return empty
    if (products.length === 0) {
      return [];
    }

    return products.map(normalizeProduct).filter(isStorefrontVisible);
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

export const getProductsAdmin = async () => {
  // If Firebase not configured, return empty
  if (!isFirebaseConfigured || !db) {
    return [];
  }

  try {
    const productsRef = collection(db, 'products');
    const querySnapshot = await getDocs(productsRef);

    const products = querySnapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    if (products.length === 0) {
      return [];
    }

    return products.map(normalizeProduct);
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

// Get product by ID
export const getProductById = async (productId) => {
  // If Firebase not configured, do not show mock product details
  if (!isFirebaseConfigured || !db) {
    return null;
  }

  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      return {
        id: productSnap.id,
        ...productSnap.data()
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching product:', error);

    return null;
  }
};

// Get products by category
export const getProductsByCategory = async (category) => {
  // If Firebase not configured, return empty
  if (!isFirebaseConfigured || !db) {
    return [];
  }

  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('category', '==', category));
    const querySnapshot = await getDocs(q);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If no products, return empty
    if (products.length === 0) {
      return [];
    }

    return products.map(normalizeProduct).filter(isStorefrontVisible).filter((p) => (p.categories || []).includes(category));
  } catch (error) {
    console.error('Error fetching products by category:', error);
    return [];
  }
};

// Get featured products
export const getFeaturedProducts = async () => {
  // If Firebase not configured, return empty
  if (!isFirebaseConfigured || !db) {
    return [];
  }

  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('featured', '==', true), limit(6));
    const querySnapshot = await getDocs(q);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // If no products, return empty
    if (products.length === 0) {
      return [];
    }

    return products.map(normalizeProduct).filter(isStorefrontVisible).filter((p) => p.featured).slice(0, 6);
  } catch (error) {
    console.error('Error fetching featured products:', error);
    return [];
  }
};

export const createProductAdmin = async (payload) => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');
  const now = serverTimestamp();

  const baseId = toProductId(payload?.name);
  let id = baseId;
  for (let i = 0; i < 50; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const existing = await getDoc(doc(db, 'products', id));
    if (!existing.exists()) break;
    id = `${baseId}-${i + 2}`;
  }
  if (!id) id = `${baseId}-${Date.now()}`;

  await setDoc(doc(db, 'products', id), { ...payload, createdAt: now, updatedAt: now });
  return { id };
};

export const upsertProductAdmin = async (id, payload) => {
  if (!id) throw new Error('Missing product id');
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');
  await setDoc(doc(db, 'products', id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
  return { id };
};

const dataUrlToBlob = async (dataUrl) => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

export const uploadProductImage = async ({ productId, file, dataUrl, filename }) => {
  if (!productId) throw new Error('Missing product id');
  if (!isFirebaseConfigured || !storage) throw new Error('Firebase not configured');

  const blob = file ? file : dataUrl ? await dataUrlToBlob(dataUrl) : null;
  if (!blob) throw new Error('Missing image');

  const safeName = String(filename || (file && file.name) || `image-${Date.now()}`)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-');
  const folder = toProductId(productId);
  const path = `products/${folder}/${Date.now()}-${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, blob);
  return await getDownloadURL(ref);
};

// Search products
export const searchProducts = async (searchTerm) => {
  try {
    const products = await getProducts();
    const term = searchTerm.toLowerCase();
    
    return products.filter(product => 
      product.name.toLowerCase().includes(term) ||
      (product.category || '').toLowerCase().includes(term) ||
      (Array.isArray(product.categories) && product.categories.some((c) => (c || '').toLowerCase().includes(term))) ||
      (String(product.description || '').toLowerCase().includes(term)) ||
      (Array.isArray(product.tags) && product.tags.some((t) => (t || '').toLowerCase().includes(term))) ||
      (Array.isArray(product.sizes) && product.sizes.some((s) => (s || '').toLowerCase().includes(term))) ||
      (Array.isArray(product.colors) && product.colors.some((c) => (c || '').toLowerCase().includes(term)))
    );
  } catch (error) {
    console.error('Error searching products:', error);
    return [];
  }
};